<template>
  <el-menu ref="menuRef" class="mixed-top-menu" :ellipsis-icon="MoreLabel" :default-active="activeModule?.path || ''" mode="horizontal" :ellipsis="true" @select="selectModule">
    <el-menu-item v-for="item in modules" :key="item.path" :index="item.path">
      {{ translateRouteTitle(item) }}
    </el-menu-item>
  </el-menu>
</template>
<script setup>
import { useRouter } from 'vue-router'
import { defineComponent, h, ref, watch, nextTick } from 'vue'
import { ArrowDown } from '@element-plus/icons-vue'
import { translatePageText } from '@/locales/pageText'
import { useMixedNavigation, firstMenuPage } from '@/composables/useMixedNavigation'
import { translateRouteTitle } from '@/locales/route'
const menuRef = ref()
const MoreLabel = defineComponent({
  setup: () => () => h('span', { class: 'topnav-more-label' }, [
    translatePageText('更多'), h(ArrowDown, { style: { width: '12px', height: '12px' } })
  ])
})
const router = useRouter()
const { modules, activeModule } = useMixedNavigation()
// Element Plus observes available width. Recalculate also when permissions or labels change.
watch(() => modules.value.map(item => `${item.path}:${translateRouteTitle(item)}`).join('|'), async () => {
  await nextTick()
  menuRef.value?.handleResize()
}, { flush: 'post' })
function selectModule(path) {
  if (activeModule.value?.path === path) return
  const page = firstMenuPage(modules.value.find(item => item.path === path))
  if (!page) return
  if (/^https?:\/\//.test(page.path)) {
    window.open(page.path, '_blank', 'noopener,noreferrer')
    return
  }
  let query = page.query
  if (typeof query === 'string') {
    try { query = JSON.parse(query) } catch { query = undefined }
  }
  router.push({ path: page.path, query })
}
</script>
<style scoped>
.mixed-top-menu {
  --el-menu-horizontal-height: 64px;
  --el-menu-bg-color: var(--sb-bg-nav);
  --el-menu-text-color: var(--sb-text-secondary);
  --el-menu-active-color: var(--sb-color-primary-text);
  flex: 1;
  min-width: 0;
  width: 0;
  flex-wrap: nowrap;
  align-items: center;
  border-bottom: 0;
}
/* Only top-level items get the compact selection shape; sidebar styles stay independent. */
.mixed-top-menu :deep(> .el-menu-item),
.mixed-top-menu :deep(> .el-sub-menu) {
  flex-shrink: 0;
  white-space: nowrap;
  height: 40px;
  margin: 0 4px;
  border-bottom: 0;
}
.mixed-top-menu :deep(> .el-menu-item),
.mixed-top-menu :deep(> .el-sub-menu > .el-sub-menu__title) {
  height: 40px;
  padding: 0 16px;
  border-bottom: 0;
  border-radius: 10px;
  font-size: 14px;
  line-height: 40px;
  transition: background-color 150ms, color 150ms;
}
.mixed-top-menu :deep(> .el-menu-item:not(.is-active):hover),
.mixed-top-menu :deep(> .el-sub-menu:not(.is-active) > .el-sub-menu__title:hover) {
  background: var(--sb-bg-hover);
  color: var(--sb-text-title);
}
.mixed-top-menu :deep(> .el-menu-item.is-active),
.mixed-top-menu :deep(> .el-sub-menu.is-active > .el-sub-menu__title) {
  background: var(--sb-color-primary-bg);
  color: var(--sb-color-primary-text);
  font-weight: 600;
  border-bottom: 0;
}
.mixed-top-menu :deep(> .el-menu-item.is-active:hover),
.mixed-top-menu :deep(> .el-sub-menu.is-active > .el-sub-menu__title:hover) {
  background: var(--sb-color-primary-bg-hover);
}
.mixed-top-menu :deep(> .el-menu-item:focus-visible),
.mixed-top-menu :deep(> .el-sub-menu > .el-sub-menu__title:focus-visible) {
  outline: 2px solid var(--sb-color-primary-text);
  outline-offset: 2px;
}
.mixed-top-menu :deep(.el-sub-menu__icon-more) {
  width: auto;
  height: auto;
  margin: 0;
  font-size: 14px;
}
.mixed-top-menu :deep(.topnav-more-label) {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  font-style: normal;
  line-height: 22px;
}
</style>
